import React from 'react';
import styled from 'styled-components';
import { Type } from '@spotify-internal/encore-creator-web';
import {
  azure,
  spacer16,
  spacer32,
  screenMdMin,
  aquamarine,
  screenMdMax,
  spacer48,
  screenXsMin,
} from '@spotify-internal/encore-foundation';
import { pageConstraintStyles } from './styles';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { MiniCarousel } from './MiniCarousel';
import { useLocalePreferenceContext } from '../../hooks/useLocalePreference';

export function Features() {
  const intl = useIntl();
  const localePreference = useLocalePreferenceContext();

  return (
    <FeaturesGrid>
      <div style={{ gridArea: 'feature-img-1' }}>
        <MiniCarousel
          previousSlideText={intl.formatMessage(carouselControlMessages.goNext)}
          nextSlideText={intl.formatMessage(carouselControlMessages.goPrevious)}
          carouselSlides={[
            <StatsSlideContainer>
              <StatsSlide>
                <img src="/images/landing_page/icon-people.svg" alt="" role="presentation" />
                <FormattedMessage
                  id="landing-page.stats.mau"
                  defaultMessage="<headline>299</headline> <subheadline>million</subheadline> <body>monthly active users</body>"
                  values={{
                    headline: (...chunks) => <StatsSlideHeadline>{chunks}</StatsSlideHeadline>,
                    subheadline: (...chunks) => (
                      <StatsSlideSubHeadline>{chunks}</StatsSlideSubHeadline>
                    ),
                    body: (...chunks) => <StatsSlideBody>{chunks}</StatsSlideBody>,
                  }}
                />
              </StatsSlide>
            </StatsSlideContainer>,
            <StatsSlideContainer>
              <StatsSlide>
                <img src="/images/landing_page/icon-arrow.svg" alt="" role="presentation" />
                <FormattedMessage
                  id="landing-page.stats.listening"
                  defaultMessage="<headline>200%</headline> <subheadline>increase</subheadline> <body>in podcast listening YoY for 2019</body>"
                  values={{
                    headline: (...chunks) => <StatsSlideHeadline>{chunks}</StatsSlideHeadline>,
                    subheadline: (...chunks) => (
                      <StatsSlideSubHeadline>{chunks}</StatsSlideSubHeadline>
                    ),
                    body: (...chunks) => <StatsSlideBody>{chunks}</StatsSlideBody>,
                  }}
                />
              </StatsSlide>
            </StatsSlideContainer>,
            <StatsSlideContainer>
              <StatsSlide>
                <img src="/images/landing_page/icon-globe.svg" alt="" role="presentation" />
                <FormattedMessage
                  id="landing-page.stats.markets"
                  defaultMessage="<headline>92</headline> <subheadline>markets</subheadline> <body>Spotify’s global reach</body>"
                  values={{
                    headline: (...chunks) => <StatsSlideHeadline>{chunks}</StatsSlideHeadline>,
                    subheadline: (...chunks) => (
                      <StatsSlideSubHeadline>{chunks}</StatsSlideSubHeadline>
                    ),
                    body: (...chunks) => <StatsSlideBody>{chunks}</StatsSlideBody>,
                  }}
                />
              </StatsSlide>
            </StatsSlideContainer>,
          ]}
        />
      </div>
      <FeatureTextContainer style={{ gridArea: 'feature-text-1' }}>
        <FeatureHeader>
          <FormattedMessage
            id="landing-page.features.be-where-millions"
            defaultMessage="Be where millions of listeners are"
          />
        </FeatureHeader>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="landing-page.features.listeners"
            defaultMessage="Spotify has over 299 million listeners across 92 markets worldwide just waiting to discover podcasts like yours. We're the second most popular place to listen to
          podcasts in the world — and growing fast. Our podcast audience has nearly doubled
          since the start of 2019."
          />
        </Type>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="landing-page.features.on-spotify-people"
            defaultMessage="On Spotify, people can stream or download your show on Android and iOS, desktop, across
          gaming consoles, cars, TVs, smart speakers, and on whatever else they use to tune in<azure>*</azure>."
            values={{
              azure: chunks => (
                <AzureLink
                  href="https://spotify-everywhere.com/pages/product-categories"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </AzureLink>
              ),
            }}
          />
        </Type>
      </FeatureTextContainer>
      <FeatureTextContainer isLeftColumn style={{ gridArea: 'feature-text-2' }}>
        <FeatureHeader>
          <FormattedMessage
            id="landing-page.features.get-to-know"
            defaultMessage="Get to know your audience"
          />
        </FeatureHeader>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="landing-page.features.because-people-are"
            defaultMessage="Because people are logged in and listening every day, Spotify gives you a powerful
          snapshot of who your listeners are, what episodes they're streaming the most, and even the
          music they love — insights that will help you plan future episodes or get sponsors."
          />
        </Type>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="landing-page.features.you-can-also"
            defaultMessage="You can also dive deeper into each episode with details around how long people listen and
          where they start and stop, all while tracking your follower growth."
          />
        </Type>
      </FeatureTextContainer>
      <div style={{ gridArea: 'feature-img-2' }}>
        <MiniCarousel
          previousSlideText={intl.formatMessage(carouselControlMessages.goNext)}
          nextSlideText={intl.formatMessage(carouselControlMessages.goPrevious)}
          carouselSlides={[
            <SlideImage
              src={`/images/landing_page/2a-${localePreference.languageAndRegionCode}.svg`}
              alt={intl.formatMessage(audienceSlideMessages.followers)}
            />,
            <SlideImage
              src={`/images/landing_page/2b-${localePreference.languageAndRegionCode}.svg`}
              alt={intl.formatMessage(audienceSlideMessages.startsStreams)}
            />,
            <SlideImage
              src={`/images/landing_page/2c-${localePreference.languageAndRegionCode}.svg`}
              alt={intl.formatMessage(audienceSlideMessages.age)}
            />,
          ]}
        />
      </div>
      <div style={{ gridArea: 'feature-img-3' }}>
        <MiniCarousel
          previousSlideText={intl.formatMessage(carouselControlMessages.goNext)}
          nextSlideText={intl.formatMessage(carouselControlMessages.goPrevious)}
          carouselSlides={[
            <SlideImage
              src="/images/landing_page/3a.png"
              alt={intl.formatMessage(creatorsSlideMessages.guysNextDoor)}
            />,
            <SlideImage
              src="/images/landing_page/3b.png"
              alt={intl.formatMessage(creatorsSlideMessages.rintikSedu)}
            />,
            <SlideImage
              src="/images/landing_page/3c.png"
              alt={intl.formatMessage(creatorsSlideMessages.upFirst)}
            />,
            <SlideImage
              src="/images/landing_page/3d.png"
              alt={intl.formatMessage(creatorsSlideMessages.grupo)}
            />,
          ]}
        />
      </div>
      <FeatureTextContainer style={{ gridArea: 'feature-text-3' }}>
        <FeatureHeader>
          <FormattedMessage
            id="landing-page.features.were-here-for"
            defaultMessage="We’re here for creators"
          />
        </FeatureHeader>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="landing-page.features.spotify-was-designed"
            defaultMessage="Spotify was designed to help artists get discovered — now we’re working to do the
            same for podcasters. Audio is what we do."
          />
        </Type>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="landing-page.features.with-editorial"
            defaultMessage="With editorial and personalized playlists (like Your Daily Podcasts, This Week's Top
            Podcasts, and Brain Snacks), we're focused on developing powerful new ways to help you
            grow your audience."
          />
        </Type>
      </FeatureTextContainer>
    </FeaturesGrid>
  );
}

const carouselControlMessages = defineMessages({
  goNext: { id: 'landing-page.features.goNext', defaultMessage: 'Go to next slide' },
  goPrevious: {
    id: 'landing-page.features.goPreviousNext',
    defaultMessage: 'Go to previous slide',
  },
});

const audienceSlideMessages = defineMessages({
  followers: {
    id: 'landing-page.features.followers',
    defaultMessage: 'Chart showing followers information',
  },
  startsStreams: {
    id: 'landing-page.features.startsStreams',
    defaultMessage: 'Chart showing starts and streams information',
  },
  age: {
    id: 'landing-page.features.age',
    defaultMessage: 'Chart showing age information',
  },
});

const creatorsSlideMessages = defineMessages({
  guysNextDoor: {
    id: 'landing-page.features.guysNextDoor',
    defaultMessage: 'The hosts of Guys Next Door',
  },
  rintikSedu: {
    id: 'landing-page.features.rintikSedu',
    defaultMessage: 'The podcaster Rintik Sedu',
  },
  upFirst: {
    id: 'landing-page.features.upFirst',
    defaultMessage: 'The hosts of Up First',
  },
  grupo: {
    id: 'landing-page.features.grupo',
    defaultMessage: 'The hosts of Grupo',
  },
});

const FeaturesGrid = styled.div`
  display: grid;
  grid-gap: ${spacer32};
  grid-template-areas:
    'feature-img-1'
    'feature-text-1'
    'feature-img-2'
    'feature-text-2'
    'feature-img-3'
    'feature-text-3';
  grid-template-columns: 1fr;
  justify-items: center;
  margin: 40px 16px;

  @media (min-width: ${screenMdMin}) {
    ${pageConstraintStyles}

    align-items: center;
    justify-items: flex-start;
    grid-gap: ${spacer16};
    grid-template-areas:
      'feature-img-1 feature-text-1'
      'feature-text-2 feature-img-2'
      'feature-img-3 feature-text-3';
    grid-template-columns: 1fr 1fr;
  }
`;

const FeatureTextContainer = styled.div<{ isLeftColumn?: boolean }>`
  @media (min-width: ${screenMdMin}) {
    max-width: 485px;
    margin-left: ${props => (props.isLeftColumn ? spacer48 : '0px')};

    @media (min-width: ${screenMdMax}) {
      margin-left: ${props => (props.isLeftColumn ? '148px' : '0px')};
    }
  }
`;

const FeatureHeader = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading2,
})`
  font-size: 28px;
  line-height: 30px;
  color: ${aquamarine};

  @media (min-width: ${screenMdMin}) {
    font-size: 54px;
    line-height: 54px;
  }
`;

const SlideImage = styled.img`
  width: 375px;

  @media (min-width: ${screenMdMax}) {
    width: 475px;
  }

  @media (max-width: ${screenXsMin}) {
    width: 200px;
  }
`;

const StatsSlideContainer = styled.div`
  display: flex;

  align-items: center;
  justify-content: center;
  background-image: url(/images/landing_page/1a-frame.png);
  width: 375px;
  height: 369px
  background-size: 375px 369px;
  padding-top: ${spacer32};

  @media (min-width: ${screenMdMax}) {
    width: 475px;
    height: 468px;
    background-size: 475px 468px;
    padding-top: ${spacer48};
  }

  @media (max-width: ${screenXsMin}) {
    width: 200px;
    height: 197px;
    background-size: 200px 197px;
    padding-top: ${spacer16};
  }
`;

const StatsSlide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  max-width: 250px;
  height: 100%;

  @media (min-width: ${screenMdMax}) {
    max-width: 300px;
  }
  @media (max-width: ${screenXsMin}) {
    max-width: 140px;
  }

  img {
    height: 60px;

    @media (min-width: ${screenMdMax}) {
      height: 80px;
    }
    @media (max-width: ${screenXsMin}) {
      height: 40px;
    }
  }
`;

const StatsSlideHeadline = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading2,
})`
  font-size: 80px;
  padding: 48px 0;
  font-weight: 500;

  @media (min-width: ${screenMdMax}) {
    font-size: 100px;
  }

  @media (max-width: ${screenXsMin}) {
    font-size: 40px
    padding: 8px 0;
  }
`;

const StatsSlideSubHeadline = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading2,
})`
  font-size: 42px;
  padding-bottom: 16px;
  font-weight: 400;

  @media (min-width: ${screenMdMax}) {
    font-size: 52px;
    padding-bottom: 28px;
  }

  @media (max-width: ${screenXsMin}) {
    font-size: 22px;
    padding-bottom: 5px;
  }
`;
const StatsSlideBody = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading4,
})`
  font-size: 18px;
  line-height: 34px;
  @media (min-width: ${screenMdMax}) {
    font-size: 30px;
  }
  @media (max-width: ${screenXsMin}) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const AzureLink = styled.a`
  color: ${azure};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
